import {
  TunnelRequirementSchema,
  type TunnelRequirement,
} from '@terra/shared-contracts-internal-network-tunnel';
import {
  INTERACTIVE_SESSION_TOOL_NAME,
  RUN_BACKGROUND_TOOL_NAME,
  RUN_COMMAND_TOOL_NAME,
  parseTerminalOutputItem,
} from '@terra/shared-dto-chat-types';
import { isInternalNetworkTunnelRoutingTorch } from '../../../../electron-shared/utils/internal-network-tunnel-routing';
import type { InternalNetworkTunnelUiStatus } from '../../../../hooks/useInternalNetworkTunnel';
import {
  buildPortalToolErrorResult,
  type PortalToolHandler,
  type PortalToolHandlerResult,
} from '../types';

export type ReadInternalNetworkTunnelStatus =
  () => Promise<InternalNetworkTunnelUiStatus | null>;

const defaultReadTunnelStatus: ReadInternalNetworkTunnelStatus = async () => {
  const api =
    typeof window !== 'undefined' ? window.internalNetworkTunnelAPI : undefined;

  if (!api) {
    return null;
  }

  return api.status();
};

const getActiveRoutingNetworkId = (
  status: InternalNetworkTunnelUiStatus | null,
): string | null => {
  if (!status || !isInternalNetworkTunnelRoutingTorch(status)) {
    return null;
  }

  return status.internalAccessNetworkId ?? null;
};

const isGuardedToolInvocation = ({
  toolName,
  ptyAction,
}: {
  toolName: string;
  ptyAction: string | undefined;
}): boolean => {
  if (
    toolName === RUN_COMMAND_TOOL_NAME ||
    toolName === RUN_BACKGROUND_TOOL_NAME
  ) {
    return true;
  }

  if (toolName === INTERACTIVE_SESSION_TOOL_NAME) {
    const resolvedAction = ptyAction ?? 'open';

    return resolvedAction === 'open' || resolvedAction === 'send';
  }

  return false;
};

const buildBlockedTunnelMessage = ({
  requirement,
  isInteractiveSessionMismatch,
}: {
  requirement: Extract<TunnelRequirement, { kind: 'required' | 'unsupported' }>;
  isInteractiveSessionMismatch: boolean;
}): string => {
  if (requirement.kind === 'unsupported') {
    return 'This external network is reached through a connector, but no internal access network is configured for it. Ask the user to select an internal access network for this surface in settings, then retry.';
  }

  if (isInteractiveSessionMismatch) {
    return `This interactive session was opened with different internal network tunnel routing than required (${requirement.networkName}). Ask the user to connect the internal network tunnel to ${requirement.networkName} in Proxy settings and reopen the interactive session.`;
  }

  return `This external network is reached through a connector. Ask the user to connect the internal network tunnel to ${requirement.networkName} in Proxy settings, then retry.`;
};

const buildWarningNotice = ({
  networkName,
  isInteractiveSessionMismatch,
}: {
  networkName: string;
  isInteractiveSessionMismatch: boolean;
}): string => {
  if (isInteractiveSessionMismatch) {
    return `[Notice: This interactive session was opened with different internal network tunnel routing than "${networkName}"; reopen the interactive session after connecting the tunnel to route through the target's internal access network.]`;
  }

  return `[Notice: Terminal command did not go through the target's internal access network "${networkName}".]`;
};

const prependNoticeToResult = ({
  handlerResult,
  notice,
}: {
  handlerResult: PortalToolHandlerResult;
  notice: string;
}): PortalToolHandlerResult => {
  const parsed = parseTerminalOutputItem(handlerResult.result);

  if (!parsed) {
    return handlerResult;
  }

  const nextOutput = parsed.output ? `${notice}\n${parsed.output}` : notice;
  const nextScreen =
    typeof parsed.screen === 'string'
      ? parsed.screen
        ? `${notice}\n${parsed.screen}`
        : notice
      : parsed.screen;

  return {
    ...handlerResult,
    result: {
      ...parsed,
      output: nextOutput,
      outputLength: nextOutput.length,
      ...(nextScreen === undefined ? {} : { screen: nextScreen }),
    },
  };
};

const withCommandOnErrorResult = ({
  errorResult,
  command,
}: {
  errorResult: PortalToolHandlerResult;
  command: string;
}): PortalToolHandlerResult => {
  const parsed = parseTerminalOutputItem(errorResult.result);

  if (!parsed || !command) {
    return errorResult;
  }

  return {
    ...errorResult,
    result: {
      ...parsed,
      command,
    },
  };
};

export const withTargetTunnelGuard = ({
  handler,
  readTunnelStatus = defaultReadTunnelStatus,
}: {
  handler: PortalToolHandler;
  readTunnelStatus?: ReadInternalNetworkTunnelStatus;
}): PortalToolHandler => {
  const openedPtyNetworks = new Map<string, string | null>();

  return async (params) => {
    const { input, toolCallId, batchId } = params;
    const toolName =
      typeof input.toolName === 'string'
        ? input.toolName
        : RUN_COMMAND_TOOL_NAME;
    const ptyAction =
      typeof input.ptyAction === 'string' ? input.ptyAction : undefined;
    const ptyId = typeof input.ptyId === 'string' ? input.ptyId : undefined;
    const displayCommand =
      typeof input.displayCommand === 'string'
        ? input.displayCommand
        : typeof input.command === 'string'
          ? input.command
          : '';

    if (
      toolName === INTERACTIVE_SESSION_TOOL_NAME &&
      ptyAction === 'close' &&
      ptyId
    ) {
      openedPtyNetworks.delete(ptyId);
    }

    if (!isGuardedToolInvocation({ toolName, ptyAction })) {
      return handler(params);
    }

    const parsedRequirement = TunnelRequirementSchema.safeParse(
      input.tunnelRequirement,
    );
    const requirement: TunnelRequirement = parsedRequirement.success
      ? parsedRequirement.data
      : { kind: 'notRequired' };

    const status = await readTunnelStatus().catch(() => null);
    const activeNetworkId = getActiveRoutingNetworkId(status);
    const isInteractiveOpen =
      toolName === INTERACTIVE_SESSION_TOOL_NAME &&
      (ptyAction === undefined || ptyAction === 'open');
    const isInteractiveSend =
      toolName === INTERACTIVE_SESSION_TOOL_NAME && ptyAction === 'send';

    const recordOpenedPtyIfPresent = (
      result: PortalToolHandlerResult,
    ): void => {
      if (!isInteractiveOpen) {
        return;
      }

      const parsed = parseTerminalOutputItem(result.result);

      if (parsed?.ptyId) {
        openedPtyNetworks.set(parsed.ptyId, activeNetworkId);
      }
    };

    if (requirement.kind === 'notRequired') {
      const result = await handler(params);

      recordOpenedPtyIfPresent(result);

      return result;
    }

    if (requirement.kind === 'unsupported') {
      if (
        requirement.reason === 'noNetwork' &&
        requirement.enforcement === 'block'
      ) {
        return withCommandOnErrorResult({
          errorResult: buildPortalToolErrorResult({
            toolName,
            toolCallId,
            batchId,
            message: buildBlockedTunnelMessage({
              requirement,
              isInteractiveSessionMismatch: false,
            }),
            failureKind: 'transport',
          }),
          command: displayCommand,
        });
      }

      const result = await handler(params);

      recordOpenedPtyIfPresent(result);

      return result;
    }

    const recordedPtyNetworkId =
      isInteractiveSend && ptyId && openedPtyNetworks.has(ptyId)
        ? (openedPtyNetworks.get(ptyId) ?? null)
        : undefined;
    const isPtyRoutingDrifted =
      isInteractiveSend &&
      recordedPtyNetworkId !== undefined &&
      (recordedPtyNetworkId !== activeNetworkId ||
        recordedPtyNetworkId !== requirement.tailscaleNetworkId);
    const isSatisfied =
      activeNetworkId === requirement.tailscaleNetworkId &&
      !isPtyRoutingDrifted;

    if (isSatisfied) {
      const result = await handler(params);

      recordOpenedPtyIfPresent(result);

      return result;
    }

    if (requirement.enforcement === 'block') {
      return withCommandOnErrorResult({
        errorResult: buildPortalToolErrorResult({
          toolName,
          toolCallId,
          batchId,
          message: buildBlockedTunnelMessage({
            requirement,
            isInteractiveSessionMismatch:
              isPtyRoutingDrifted || isInteractiveSend,
          }),
          failureKind: 'transport',
        }),
        command: displayCommand,
      });
    }

    const result = await handler(params);

    recordOpenedPtyIfPresent(result);

    return prependNoticeToResult({
      handlerResult: result,
      notice: buildWarningNotice({
        networkName: requirement.networkName,
        isInteractiveSessionMismatch: isPtyRoutingDrifted || isInteractiveSend,
      }),
    });
  };
};
